import { CSSResultGroup, html, LitElement } from 'lit'
import type { PropertyValues } from 'lit'
import { consume } from '@lit/context'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { live } from 'lit/directives/live.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './compare-checkbox.style.js'
import { CscCompareToggleEvent } from 'components/discovery/compare/compare-events.js'
import { compareSelectionHasProductId } from 'components/discovery/compare/compare-selection-storage.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, ProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { PriceSource } from 'components/discovery/product/price/price.js'

@customElement('csc-compare-checkbox')
export class CSCCompareCheckbox extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @consume({ context: productTileProductContext })
  @state()
  product: InternalProduct = new ProductModel().parseToInternal()

  @property({ type: String, attribute: 'locale' })
  locale: string = 'en-US'

  @property({ type: String })
  variant: 'default' | 'with-background' = 'default'

  @property({ type: String, attribute: 'price-source' })
  priceSource: PriceSource = 'sale'

  @property({ type: Number, attribute: 'product-id' })
  productId = 0

  @property({ type: String, attribute: 'product-name' })
  productName = ''

  @property({ type: String, attribute: 'product-code' })
  productCode = ''

  @property({ type: String, attribute: 'product-image' })
  productImage = ''

  @property({ type: String, attribute: 'product-price' })
  productPrice = ''

  @state()
  private checked = false

  /** Avoid re-applying session selection on every product context refresh. */
  private lastHydratedSelectionId = -1

  private getSelectionProductId(): number {
    if (this.productId > 0) return this.productId
    const fromCtx = this.product?.id
    return typeof fromCtx === 'number' && fromCtx > 0 ? fromCtx : 0
  }

  private syncCheckedFromStorage() {
    const id = this.getSelectionProductId()
    if (id <= 0 || this.lastHydratedSelectionId === id) return
    this.lastHydratedSelectionId = id
    this.checked = compareSelectionHasProductId(id)
  }

  private handleListingNavigation = (): void => {
    this.lastHydratedSelectionId = -1
    this.syncCheckedFromStorage()
  }

  override connectedCallback() {
    super.connectedCallback()
    window.addEventListener('csc-compare-rejected', this.handleRejected as EventListener)
    window.addEventListener('csc-compare-remove', this.handleExternalRemove as EventListener)
    window.addEventListener('csc-compare-clear', this.handleClear)
    window.addEventListener('popstate', this.handleListingNavigation)
    this.syncCheckedFromStorage()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('csc-compare-rejected', this.handleRejected as EventListener)
    window.removeEventListener('csc-compare-remove', this.handleExternalRemove as EventListener)
    window.removeEventListener('csc-compare-clear', this.handleClear)
    window.removeEventListener('popstate', this.handleListingNavigation)
  }

  private handleRejected = (e: CustomEvent<{ id: number }>) => {
    if (e.detail.id === this.productId) {
      this.checked = false
    }
  }

  private handleExternalRemove = (e: CustomEvent<{ id: number }>) => {
    if (e.detail.id === this.productId) {
      this.checked = false
    }
  }

  private handleClear = () => {
    this.checked = false
  }

  override updated(changed: PropertyValues<this>) {
    super.updated(changed)
    if (changed.has('product') || changed.has('productId')) {
      this.syncCheckedFromStorage()
    }
  }

  private getPriceValue(): string | number | null {
    switch (this.priceSource) {
      case 'sale': return this.product.prices.sale ?? null
      case 'consumer': return this.product.prices.consumer ?? null
      case 'customer': return this.product.prices.customer ?? null
      case 'list': return this.product.prices.list ?? null
      case 'sale-taxed': return this.product.prices.saleTaxed ?? null
      case 'sale-display': return this.product.prices.saleDisplay ?? null
      case 'per-price-uom': return (this.product.prices.sale && this.product.prices.pricePerPriceUom) || (this.product.prices.sale ?? null)
      default: return this.product.prices.sale ?? null
    }
  }

  private formatPrice(value: string | number | null): string {
    if (value == null || value === '') return ''
    const num = Number(value)
    if (Number.isNaN(num)) return String(value)
    try {
      return Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.product.prices.currencyCode || 'EUR',
      }).format(num)
    } catch {
      return String(value)
    }
  }

  private handleChange() {
    this.checked = !this.checked
    window.dispatchEvent(new CscCompareToggleEvent({
      product: {
        id: this.productId,
        name: this.productName,
        code: this.productCode,
        price: this.productPrice || this.formatPrice(this.getPriceValue()),
        image: this.product.picture.mini,
      },
      checked: this.checked,
    }))
  }

  override render() {
    const classes = {
      'csc-compare-checkbox': true,
      'has-background': this.variant === 'with-background'
    }
    return html`
      <div class=${classMap(classes)}>
        <cse-checkbox
          .checked=${live(this.checked)}
          @change=${this.handleChange}
        >
          ${t('label', { ns: 'csc-compare-checkbox', default: 'Compare' })}
        </cse-checkbox>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-compare-checkbox': CSCCompareCheckbox
  }
}
